import { LIST_GC_TIME_MS, LIST_STALE_TIME_MS, useApiClient, useUtcToday } from '@klareospace/pharos-lib-front-utils';
import type { Kpi, OrderKpiMetrics } from '@klareospace/pharos-lib-types';
import { toUtcDayBound } from '@klareospace/pharos-lib-utils';
import { useQuery } from '@tanstack/react-query';

import { useMasterContext } from '&/master';

import { createKpisApi, kpiKeys } from '../api';
import { toOrderKpiMetrics } from './order-kpi-metrics.utils';
import { resolveKpiActiveRefetchInterval } from './resolve-kpi-refetch-interval';
import { useIsKpiReadEnabled } from './use-kpi-read-access';
import { useOrderKpisTodayRealtimeSync } from './use-order-kpis-today-realtime-sync';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrderKpisTodayScope {
  siteId?: string;
  spaceId?: string;
}

export interface UseOrderKpisTodayOptions {
  /** True while the home "Today" tab is active — enables a 60s heartbeat refetch once computed. */
  heartbeat?: boolean;
}

export interface UseOrderKpisTodayResult {
  /** When the KPI was last computed — absent until the first successful pipeline run. */
  computedAt: Date | undefined;
  /** True when the `feat-dashboard` flag is active AND the user holds `kpis:read`. */
  isEnabled: boolean;
  isError: boolean;
  isLoading: boolean;
  metrics: OrderKpiMetrics | undefined;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Fetches today's `order-kpis` KPI for the current scope, gated on `feat-dashboard` + `kpis:read`. */
export function useOrderKpisToday(options: UseOrderKpisTodayOptions = {}): UseOrderKpisTodayResult {
  const { heartbeat = false } = options;
  const client = useApiClient('deliveries');
  const api = createKpisApi(client);
  const { siteId, spaceId } = useMasterContext();
  const isEnabled = useIsKpiReadEnabled();
  const utcNow = useUtcToday();
  const bound = toUtcDayBound(utcNow);
  const scope: OrderKpisTodayScope = { siteId: siteId ?? undefined, spaceId: spaceId ?? undefined };

  const query = useQuery({
    enabled: isEnabled,
    gcTime: LIST_GC_TIME_MS,
    queryFn: (): Promise<Kpi> => api.getOrderKpisDay({ ...bound, ...scope }),
    queryKey: kpiKeys.day({ ...bound, ...scope }),
    refetchInterval: resolveKpiActiveRefetchInterval(heartbeat),
    staleTime: LIST_STALE_TIME_MS,
  });

  useOrderKpisTodayRealtimeSync({ bound, enabled: isEnabled, scope });

  return {
    computedAt: query.data?.computedAt,
    isEnabled,
    isError: query.isError,
    isLoading: query.isLoading,
    metrics: toOrderKpiMetrics(query.data?.metrics),
  };
}
